// src/events/manage_venues_dashboard.js
import React, { useEffect, useState } from "react";
import {
  FiPlusCircle,
  FiCalendar,
  FiCheckCircle,
  FiUser,
  FiHash,
  FiX,
  FiTrash2,
} from "react-icons/fi";
import { FaHotel } from "react-icons/fa";
import Logo from "../assets/events-logo.png";

/* Firestore */
import {
  collection,
  serverTimestamp,
  getDocs,
  query as fsQuery,
  where,
  limit,
  doc,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";

/* Storage — NEW: we now upload files and store URLs & paths */
import { getStorage, ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";

/* Profile drawer content */
import OrganiserProfile from "./organiser_profile";

/* NEW: Linked tab pages */
import MangeVenueDetsils from "./mange_venue_detsils";
import VenueBookingDetails from "./venue_booking_details";
import CompletedVenueBookings from "./completed_venue_bookings";

/* ──────────────────────────────────────────────────────────
   THEME — same as app
   ────────────────────────────────────────────────────────── */
const COLORS = {
  primary: "#f20c53",
  secondary: "#fb6c33",
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  page: "#f5f6f8",
  card: "#ffffff",
};

/* ──────────────────────────────────────────────────────────
   TABS (under header)
   ────────────────────────────────────────────────────────── */
const TABS = [
  { key: "ADD", label: "Add Venue", icon: <FiPlusCircle /> },
  { key: "MANAGE", label: "Manage Venue", icon: <FaHotel /> },
  { key: "BOOKINGS", label: "Bookings", icon: <FiCalendar /> },
  { key: "COMPLETE", label: "Complete Bookings", icon: <FiCheckCircle /> },
];

/* ──────────────────────────────────────────────────────────
   STEP LABELS (for progress text)
   ────────────────────────────────────────────────────────── */
const STEP_LABELS = [
  "Account & Ownership",
  "Venue Basics",
  "Location",
  "Operations & Timings",
  "Safety & Security",
  "Power & Internet",
  "Licences & Permissions",
  "Spaces / Halls",
  "Staffing",
  "Pricing & Rental Rules",
  "F&B & Bar",
  "Media (Photos & Files)",
  "Payouts & Billing",
  "Contacts & Notifications",
  "Declarations",
];

/* ──────────────────────────────────────────────────────────
   SESSION + ORGANISER HELPERS (same logic as events dash)
   ────────────────────────────────────────────────────────── */
const COLL_SIGNUPS = "organiser_signup";

async function findSignupByMobile(mobile) {
  const col = collection(db, COLL_SIGNUPS);
  let snap = await getDocs(
    fsQuery(col, where("general.contact.mobile", "==", mobile), limit(1))
  );
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  }
  snap = await getDocs(fsQuery(col, where("mobileVerified", "==", mobile), limit(1)));
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  }
  return null;
}

function pickContactName(signup) {
  if (!signup) return "—";
  const g = signup.general || {};
  const c = g.contact || {};
  return c.name || c.fullName || signup.contactName || signup.name || "—";
}

function pickOrganiserUID(signup) {
  if (!signup) return "—";
  return signup.organiser_UID || signup.organiserUID || signup.uid || signup.orgUID || "—";
}

function getSessionMobile() {
  try {
    const raw = localStorage.getItem("events_session");
    const j = raw ? JSON.parse(raw) : null;
    const m = j?.mobile || j?.phone || "";
    return String(m).replace(/\D/g, "").slice(-10);
  } catch {
    return "";
  }
}

export default function ManageVenuesDashboard() {
  const [activeTab, setActiveTab] = useState("MANAGE");

  // organiser header state
  const [contactName, setContactName] = useState("—");
  const [organiserUID, setOrganiserUID] = useState("—");
  const [loadingHeader, setLoadingHeader] = useState(true);

  // profile drawer
  const [profileOpen, setProfileOpen] = useState(false);

  // load organiser header
  useEffect(() => {
    let mounted = true;
    (async () => {
      const mobile = getSessionMobile();
      if (!mobile) {
        setLoadingHeader(false);
        return;
      }
      try {
        const signup = await findSignupByMobile(mobile);
        if (!mounted) return;
        setContactName(pickContactName(signup));
        setOrganiserUID(pickOrganiserUID(signup));
      } catch (e) {
        console.warn("Signup fetch failed:", e);
      } finally {
        mounted && setLoadingHeader(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // ESC to close profile
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setProfileOpen(false);
    }
    if (profileOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [profileOpen]);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: COLORS.page,
        display: "grid",
        gridTemplateRows: "72px auto 1fr",
      }}
    >
      {/* Header */}
      <header
        style={{
          background: "#1f2937",
          color: "#fff",
          display: "grid",
          gridTemplateColumns: "220px 1fr auto 56px",
          alignItems: "center",
          gap: 16,
          padding: "0 16px",
          borderBottom: `1px solid rgba(255,255,255,0.08)`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={Logo} alt="Events" style={{ height: 36 }} />
        </div>

        <div
          style={{
            textAlign: "center",
            fontWeight: 900,
            fontSize: 28,
            letterSpacing: 0.2,
          }}
        >
          Manage Venues
        </div>

        {/* Right header area — organiser info */}
        <div style={{ display: "grid", gap: 4, justifyItems: "end" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
            <FiUser />
            <span>{loadingHeader ? "Loading…" : `Hi, ${contactName}`}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
            <FiHash />
            <span>{loadingHeader ? "—" : organiserUID}</span>
          </div>
        </div>

        {/* Profile icon — opens drawer */}
        <div
          title="Open profile"
          onClick={() => setProfileOpen(true)}
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
            display: "grid",
            placeItems: "center",
            border: "1px solid rgba(255,255,255,0.25)",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <FiUser />
        </div>
      </header>

      {/* Tabs under header */}
      <div style={{ maxWidth: 1100, width: "100%", margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 10,
            margin: "12px auto 14px",
          }}
        >
          {TABS.map((t) => {
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setActiveTab(t.key);
                  // Linked in-page: render the requested component below
                }}
                style={{
                  borderRadius: 999,
                  padding: "10px 16px",
                  border: `1px solid ${active ? "transparent" : COLORS.border}`,
                  background: active
                    ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`
                    : "#fff",
                  color: active ? "#fff" : COLORS.text,
                  fontWeight: 800,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: active
                    ? "0 12px 26px rgba(242,12,83,.20)"
                    : "0 1px 2px rgba(0,0,0,.05)",
                  transition: "transform .15s ease, box-shadow .15s ease",
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <span style={{ display: "grid", placeItems: "center" }}>{t.icon}</span>
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <main style={{ padding: "0 16px 32px" }}>
        {/* ADD VENUE → wizard */}
        {activeTab === "ADD" && <VenueWizard />}

        {/* MANAGE VENUE → linked page */}
        {activeTab === "MANAGE" && <MangeVenueDetsils />}

        {/* BOOKINGS → linked page */}
        {activeTab === "BOOKINGS" && <VenueBookingDetails />}

        {/* COMPLETE BOOKINGS → linked page */}
        {activeTab === "COMPLETE" && <CompletedVenueBookings />}
      </main>

      {/* Profile Drawer */}
      {profileOpen && (
        <div style={drawerBackdrop} onClick={() => setProfileOpen(false)}>
          <div
            style={{ ...drawerPanel, transform: "translateX(0)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={drawerHeader}>
              <div style={{ fontWeight: 900 }}>Profile</div>
              <button
                type="button"
                onClick={() => setProfileOpen(false)}
                style={drawerCloseBtn}
                aria-label="Close profile panel"
              >
                <FiX />
              </button>
            </div>

            <div style={drawerBody}>
              <OrganiserProfile organiserUID={organiserUID} contactName={contactName} />
            </div>
          </div>

          {/* off-screen panel to preserve slide-in animation */}
          <div
            style={{
              ...drawerPanel,
              transform: "translateX(100%)",
              pointerEvents: "none",
              visibility: "hidden",
            }}
          />
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   Venue Wizard — uploads to Storage + URLs in Firestore
   Duplicate check: prevent re-register for same organiser+venue
   ────────────────────────────────────────────────────────── */
function VenueWizard() {
  const [step, setStep] = useState(0); // 0..14
  const total = STEP_LABELS.length;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showThanks, setShowThanks] = useState(false);
  const [showAlready, setShowAlready] = useState(false);

  // Files (we will UPLOAD these now)
  const [files, setFiles] = useState({
    ownership: {
      gst: null,
      pan: null,
      incorporationCert: null,
      tradeLicense: null,
    },
    safety: {
      insuranceDocs: [],
    },
    media: {
      logo: null,
      heroBanner: null,
      entrance: [],
      outdoor: [],
      frontSide: [],
      banquetHalls: [],
      gallery: [],
      floorPlans: [],
    },
  });

  // Core data
  const [data, setData] = useState({
    ownership: {
      legalEntityName: "",
      venuePublicName: "",
      ownerName: "",
      primaryContact: { name: "", role: "", phone: "", email: "" },
      gstin: "",
      pan: "",
      incorporation: { type: "", registrationNo: "" },
      tradeLicense: { number: "", authority: "", expiry: "" },
    },
    basics: {
      venueType: "indoor",
      tagline: "",
      description: "",
      allowsFnbSales: true,
      allowsAlcoholSales: true,
      rentalPolicy: "half_or_full",
      instantBooking: false,
    },
    location: {
      address: { building: "", street: "", area: "", city: "", state: "", pin: "" },
      lat: "",
      lng: "",
      landmark: "",
      parking: { cars: "", bikes: "", valet: "false", charges: "" },
      nearestHospital: { name: "", distanceKm: "", phone: "" },
      nearestPoliceStation: { name: "", distanceKm: "", phone: "" },
    },
    operations: { openTime: "", closeTime: "", musicCutoff: "", blackoutNotes: "", siteVisitHours: "" },
    safetySecurity: {
      emergencyExits: "",
      fireAlarm: "false",
      cctv: { present: "false", cameraCount: "" },
      securityStaff: { available: "false", per100Guests: "" },
      bouncers: { inHouse: "false", availableCount: "" },
    },
    powerInternet: {
      powerKva: "",
      backup: { type: "generator", capacityKva: "" },
      threePhase: "false",
      internet: { available: "false", upMbps: "", downMbps: "" },
    },
    licences: {
      requiredCsv: "PPL, Novex",
      providedByVenue: {},
      policePermission: { required: "false", obtainedByVenue: "depends" },
    },
    spaces: [],
    staffing: { servingStaffCount: "", technicalStaffCount: "", housekeepingDuringEvent: "false", housekeepingFrequency: "" },
    pricing: {
      allowedSlots: { halfDay: true, fullDay: true },
      halfDay: { slot1: { start: "08:00", end: "14:00" }, slot2: { start: "15:00", end: "23:00" } },
      fullDay: { start: "08:00", end: "23:00" },
      basePrice: { weekday: { halfDay: "", fullDay: "" }, weekend: { halfDay: "", fullDay: "" } },
      includes: "Chairs, Tables, Basic PA, Housekeeping",
      overtimeRatePerHour: "",
      securityDeposit: "",
      cancellationPolicy: [{ daysBefore: "30", refundPercent: "80" }],
      setupBufferMinutes: "60",
      teardownBufferMinutes: "60",
      taxes: { gstPercent: "18", serviceChargePercent: "0" },
    },
    fnbBar: {
      inHouseCatering: "true",
      cuisinesCsv: "",
      perHeadPackages: [{ name: "", price: "" }],
      outsideCateringAllowed: "false",
      outsideCorkage: "",
      kitchen: { type: "finishing", fuel: "gas" },
      alcohol: { allowed: "true", barLicense: "true", lastPourTime: "22:30" },
      barCharges: { corkagePerBottle: "", bartenderPerShift: "" },
    },
    media: {},
    payoutsBilling: {
      beneficiaryName: "",
      bank: { accountName: "", accountNumber: "", ifsc: "" },
      upiId: "",
      billingAddress: "",
      invoiceIssuer: "Venue",
    },
    contacts: {
      booking: { name: "", phone: "", email: "" },
      finance: { name: "", phone: "", email: "" },
      technical: { name: "", phone: "", email: "" },
      whatsappUpdates: "",
    },
    declarations: { capacityExcludesStaff: false, termsAccepted: false, mediaConsent: false },
  });

  const setVal = (path, value) => setData((prev) => deepSet({ ...prev }, path, value));
  const setFilesVal = (path, value) => setFiles((prev) => deepSet({ ...prev }, path, value));

  const next = () => setStep((s) => Math.min(total - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  async function submitAll(e) {
    e.preventDefault();
    setError("");

    // Quick requireds
    if (!data.ownership.venuePublicName.trim()) return setError("Enter venue brand name.");
    if (!data.ownership.primaryContact.phone.trim()) return setError("Enter contact phone.");
    if (!data.location.address.city.trim() || !data.location.address.state.trim())
      return setError("Enter city and state.");

    const mobile = getSessionMobile() || "unknown";

    // ── Duplicate check: same organiser (mobile) + same venue public name
    try {
      const coll = collection(db, "new_venues");
      let snap;
      try {
        snap = await getDocs(
          fsQuery(
            coll,
            where("createdByMobile", "==", mobile),
            where("ownership.venuePublicName", "==", data.ownership.venuePublicName.trim()),
            limit(1)
          )
        );
      } catch {
        // Fallback if composite index missing: filter client-side
        const s = await getDocs(fsQuery(coll, where("createdByMobile", "==", mobile)));
        const found = s.docs.find(
          (d) => (d.data()?.ownership?.venuePublicName || "") === data.ownership.venuePublicName.trim()
        );
        snap = { empty: !found };
      }
      if (snap && !snap.empty) {
        setShowAlready(true);
        return;
      }
    } catch (err) {
      console.warn("Duplicate check failed (continuing):", err);
    }

    setSaving(true);

    // Prepare Storage + docRef (so we can use venueId in paths)
    const storage = getStorage();
    const collRef = collection(db, "new_venues");
    const docRef = doc(collRef);
    const venueId = docRef.id;

    // Helpers
    const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const bool = (v) => v === true || v === "true";

    // Convert CSV to arrays
    const licencesRequired = (data.licences.requiredCsv || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const cuisines = (data.fnbBar.cuisinesCsv || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    // ── Uploads — keep original names, store path+url
    async function uploadFile(folder, file) {
      if (!file) return null;
      const clean = sanitizeFileName(file.name);
      const path = `venues/${venueId}/${folder}/${clean}`;
      const ref = sRef(storage, path);
      await uploadBytes(ref, file);
      const url = await getDownloadURL(ref);
      return { name: clean, path, url };
    }
    async function uploadFiles(folder, fileList) {
      const arr = Array.isArray(fileList) ? fileList : [];
      const out = [];
      for (const f of arr) {
        const up = await uploadFile(folder, f);
        if (up) out.push(up);
      }
      return out;
    }

    try {
      // Ownership docs
      const up_gst = await uploadFile("ownership", files.ownership.gst);
      const up_pan = await uploadFile("ownership", files.ownership.pan);
      const up_incorp = await uploadFile("ownership", files.ownership.incorporationCert);
      const up_trade = await uploadFile("ownership", files.ownership.tradeLicense);

      // Safety docs
      const up_insurance = await uploadFiles("safety", files.safety.insuranceDocs);

      // Media
      const up_logo = await uploadFile("media", files.media.logo);
      const up_hero = await uploadFile("media", files.media.heroBanner);
      const up_entrance = await uploadFiles("media/entrance", files.media.entrance);
      const up_outdoor = await uploadFiles("media/outdoor", files.media.outdoor);
      const up_front = await uploadFiles("media/frontSide", files.media.frontSide);
      const up_banquets = await uploadFiles("media/banquetHalls", files.media.banquetHalls);
      const up_gallery = await uploadFiles("media/gallery", files.media.gallery);
      const up_floorplans = await uploadFiles("media/floorPlans", files.media.floorPlans);

      // Build payload with URLs/paths
      const payload = {
        status: "pending",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),

        createdByMobile: mobile,
        venueNameLower: data.ownership.venuePublicName.trim().toLowerCase(),

        ownership: {
          ...data.ownership,
          uploads: {
            gst: up_gst, // {name, path, url}
            pan: up_pan,
            incorporationCertificate: up_incorp,
            tradeLicense: up_trade,
          },
        },

        basics: {
          ...data.basics,
          allowsFnbSales: bool(data.basics.allowsFnbSales),
          allowsAlcoholSales: bool(data.basics.allowsAlcoholSales),
          instantBooking: bool(data.basics.instantBooking),
        },

        location: {
          ...data.location,
          lat: num(data.location.lat),
          lng: num(data.location.lng),
          parking: {
            cars: num(data.location.parking.cars),
            bikes: num(data.location.parking.bikes),
            valet: bool(data.location.parking.valet),
            charges: num(data.location.parking.charges),
          },
        },

        operations: { ...data.operations },

        safetySecurity: {
          ...data.safetySecurity,
          emergencyExits: num(data.safetySecurity.emergencyExits),
          fireAlarm: bool(data.safetySecurity.fireAlarm),
          cctv: {
            present: bool(data.safetySecurity.cctv.present),
            cameraCount: num(data.safetySecurity.cctv.cameraCount),
          },
          securityStaff: {
            available: bool(data.safetySecurity.securityStaff.available),
            per100Guests: num(data.safetySecurity.securityStaff.per100Guests),
          },
          bouncers: {
            inHouse: bool(data.safetySecurity.bouncers.inHouse),
            availableCount: num(data.safetySecurity.bouncers.availableCount),
          },
          uploads: {
            insuranceDocs: up_insurance, // array of {name,path,url}
          },
        },

        powerInternet: {
          powerKva: num(data.powerInternet.powerKva),
          backup: {
            type: data.powerInternet.backup.type,
            capacityKva: num(data.powerInternet.backup.capacityKva),
          },
          threePhase: bool(data.powerInternet.threePhase),
          internet: {
            available: bool(data.powerInternet.internet.available),
            uploadMbps: num(data.powerInternet.internet.upMbps),
            downloadMbps: num(data.powerInternet.internet.downMbps),
          },
        },

        licences: {
          required: licencesRequired,
          providedByVenue: data.licences.providedByVenue,
          policePermission: {
            required: bool(data.licences.policePermission.required),
            obtainedByVenue: data.licences.policePermission.obtainedByVenue,
          },
        },

        spaces: data.spaces.map((s) => ({
          id: s.id,
          name: s.name,
          type: s.type,
          dimensions: { lengthM: num(s.lengthM), widthM: num(s.widthM), heightM: num(s.heightM) },
          areaSqFt: num(s.areaSqFt),
          stage: s.stageAvailable
            ? { available: true, lengthM: num(s.stageL), widthM: num(s.stageW), heightM: num(s.stageH) }
            : { available: false },
          paSystem: bool(s.paSystem),
          paDetails: s.paDetails || "",
          ledWall: bool(s.ledWall),
          ledWallSize: { widthM: num(s.ledWallW), heightM: num(s.ledWallH), resolutionPx: s.ledWallRes || "" },
          inHouseDecor: bool(s.inHouseDecor),
          tables: num(s.tables),
          chairs: num(s.chairs),
          layouts: {
            theatre: num(s.layoutTheatre),
            banquet: num(s.layoutBanquet),
            classroom: num(s.layoutClassroom),
            uShape: num(s.layoutU),
            cabaret: num(s.layoutCabaret),
            standing: num(s.layoutStanding),
          },
          seatingCapacity: num(s.seatingCapacity),
          standingCapacity: num(s.standingCapacity),
          totalCapacityGuests: num(s.totalCapacityGuests),
          greenRooms: num(s.greenRooms),
          restrooms: { male: num(s.restMale), female: num(s.restFemale), accessible: num(s.restAccessible) },
          accessibility: { ramp: bool(s.accRamp), liftToFloor: bool(s.accLift) },
          notes: s.notes || "",
        })),

        staffing: {
          servingStaffCount: num(data.staffing.servingStaffCount),
          technicalStaffCount: num(data.staffing.technicalStaffCount),
          housekeepingDuringEvent: bool(data.staffing.housekeepingDuringEvent),
          housekeepingFrequency: data.staffing.housekeepingFrequency || "",
        },

        pricing: {
          allowedSlots: Object.keys(data.pricing.allowedSlots).filter((k) => data.pricing.allowedSlots[k]),
          halfDay: data.pricing.halfDay,
          fullDay: data.pricing.fullDay,
          basePrice: {
            weekday: {
              halfDay: num(data.pricing.basePrice.weekday.halfDay),
              fullDay: num(data.pricing.basePrice.weekday.fullDay),
            },
            weekend: {
              halfDay: num(data.pricing.basePrice.weekend.halfDay),
              fullDay: num(data.pricing.basePrice.weekend.fullDay),
            },
          },
          includes: data.pricing.includes,
          overtimeRatePerHour: num(data.pricing.overtimeRatePerHour),
          securityDeposit: num(data.pricing.securityDeposit),
          cancellationPolicy: data.pricing.cancellationPolicy.map((r) => ({
            daysBefore: num(r.daysBefore),
            refundPercent: num(r.refundPercent),
          })),
          setupBufferMinutes: num(data.pricing.setupBufferMinutes),
          teardownBufferMinutes: num(data.pricing.teardownBufferMinutes),
          taxes: {
            gstPercent: num(data.pricing.taxes.gstPercent),
            serviceChargePercent: num(data.pricing.taxes.serviceChargePercent),
          },
        },

        fnbBar: {
          inHouseCatering: bool(data.fnbBar.inHouseCatering),
          cuisines,
          perHeadPackages: data.fnbBar.perHeadPackages.map((p) => ({ name: p.name, price: num(p.price) })),
          outsideCateringAllowed: bool(data.fnbBar.outsideCateringAllowed),
          outsideCorkage: num(data.fnbBar.outsideCorkage),
          kitchen: data.fnbBar.kitchen,
          alcohol: {
            allowed: bool(data.fnbBar.alcohol.allowed),
            barLicense: bool(data.fnbBar.alcohol.barLicense),
            lastPourTime: data.fnbBar.alcohol.lastPourTime,
          },
          barCharges: {
            corkagePerBottle: num(data.fnbBar.barCharges.corkagePerBottle),
            bartenderPerShift: num(data.fnbBar.barCharges.bartenderPerShift),
          },
        },

        media: {
          uploads: {
            logo: up_logo,
            heroBanner: up_hero,
            entrance: up_entrance,
            outdoor: up_outdoor,
            frontSide: up_front,
            banquetHalls: up_banquets,
            gallery: up_gallery,
            floorPlans: up_floorplans,
          },
        },

        payoutsBilling: { ...data.payoutsBilling, bank: { ...data.payoutsBilling.bank } },
        contacts: data.contacts,
        declarations: data.declarations,
      };

      // Save with our chosen ID so paths align
      await setDoc(docRef, payload);
      setShowThanks(true);
    } catch (err) {
      console.error(err);
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submitAll} style={wizardWrap}>
      <ProgressBar step={step} total={total} label={STEP_LABELS[step]} />

      {error && <div style={errorBox}>{error}</div>}

      {/* ====== STEPS (same UI as before) ====== */}
      {/* 0) Account & Ownership */}
      {step === 0 && (
        <Card title="Account & Ownership" icon={<FiCheckCircle />}>
          <Grid2>
            <Field label="Legal entity name" value={data.ownership.legalEntityName} onChange={(v) => setVal(["ownership", "legalEntityName"], v)} required />
            <Field label="Venue brand name (public)" value={data.ownership.venuePublicName} onChange={(v) => setVal(["ownership", "venuePublicName"], v)} required />
          </Grid2>
          <Grid2>
            <Field label="Owner name" value={data.ownership.ownerName} onChange={(v) => setVal(["ownership", "ownerName"], v)} />
            <Field label="Primary contact name" value={data.ownership.primaryContact.name} onChange={(v) => setVal(["ownership", "primaryContact", "name"], v)} />
          </Grid2>
          <Grid3>
            <Field label="Role" value={data.ownership.primaryContact.role} onChange={(v) => setVal(["ownership", "primaryContact", "role"], v)} />
            <Field label="Phone" value={data.ownership.primaryContact.phone} onChange={(v) => setVal(["ownership", "primaryContact", "phone"], v)} />
            <Field label="Email" value={data.ownership.primaryContact.email} onChange={(v) => setVal(["ownership", "primaryContact", "email"], v)} />
          </Grid3>
          <Grid2>
            <Field label="GSTIN" value={data.ownership.gstin} onChange={(v) => setVal(["ownership", "gstin"], v)} />
            <Field label="PAN" value={data.ownership.pan} onChange={(v) => setVal(["ownership", "pan"], v)} />
          </Grid2>

          {/* Uploads */}
          <Grid2>
            <FileInput label="Upload GST (PDF/JPG)" accept=".pdf,image/*" onChange={(arr) => setFilesVal(["ownership", "gst"], arr[0] || null)} />
            <FileInput label="Upload PAN (PDF/JPG)" accept=".pdf,image/*" onChange={(arr) => setFilesVal(["ownership", "pan"], arr[0] || null)} />
          </Grid2>
          <Grid3>
            <Select
              label="Incorporation type"
              value={data.ownership.incorporation.type}
              onChange={(v) => setVal(["ownership", "incorporation", "type"], v)}
              options={[
                { value: "Private Limited", label: "Private Limited" },
                { value: "LLP", label: "LLP" },
                { value: "Partnership", label: "Partnership" },
                { value: "Proprietorship", label: "Proprietorship" },
              ]}
            />
            <Field label="CIN/Reg. No." value={data.ownership.incorporation.registrationNo} onChange={(v) => setVal(["ownership", "incorporation", "registrationNo"], v)} />
            <FileInput label="Incorporation certificate (PDF/JPG)" accept=".pdf,image/*" onChange={(arr) => setFilesVal(["ownership", "incorporationCert"], arr[0] || null)} />
          </Grid3>
          <Grid3>
            <Field label="Trade licence no." value={data.ownership.tradeLicense.number} onChange={(v) => setVal(["ownership", "tradeLicense", "number"], v)} />
            <Field label="Issuing authority" value={data.ownership.tradeLicense.authority} onChange={(v) => setVal(["ownership", "tradeLicense", "authority"], v)} />
            <Field label="Expiry" type="date" value={data.ownership.tradeLicense.expiry} onChange={(v) => setVal(["ownership", "tradeLicense", "expiry"], v)} />
          </Grid3>
          <Grid1>
            <FileInput label="Trade licence (PDF/JPG)" accept=".pdf,image/*" onChange={(arr) => setFilesVal(["ownership", "tradeLicense"], arr[0] || null)} />
          </Grid1>
        </Card>
      )}

      {/* 1..14 — unchanged UI (Basics → Declarations) */}
      {step === 1 && (
        <Card title="Venue Basics" icon={<FaHotel />}>
          <Grid3>
            <Select
              label="Venue type"
              value={data.basics.venueType}
              onChange={(v) => setVal(["basics", "venueType"], v)}
              options={[
                { value: "indoor", label: "Indoor" },
                { value: "outdoor", label: "Outdoor" },
                { value: "mixed", label: "Mixed" },
              ]}
            />
            <Select
              label="Rental policy"
              value={data.basics.rentalPolicy}
              onChange={(v) => setVal(["basics", "rentalPolicy"], v)}
              options={[
                { value: "half_day_only", label: "Half-day only" },
                { value: "full_day_only", label: "Full-day only" },
                { value: "half_or_full", label: "Half or Full" },
              ]}
            />
            <Select
              label="Instant booking"
              value={String(data.basics.instantBooking)}
              onChange={(v) => setVal(["basics", "instantBooking"], v === "true")}
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No (enquiry only)" },
              ]}
            />
          </Grid3>
          <Grid2>
            <Field label="Short tagline" value={data.basics.tagline} onChange={(v) => setVal(["basics", "tagline"], v)} />
            <Select
              label="F&B sales on-site"
              value={String(data.basics.allowsFnbSales)}
              onChange={(v) => setVal(["basics", "allowsFnbSales"], v === "true")}
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
            />
          </Grid2>
          <Grid1>
            <TextArea label="Public description" value={data.basics.description} onChange={(v) => setVal(["basics", "description"], v)} rows={4} />
          </Grid1>
          <Grid1>
            <Select
              label="Alcohol served/sold"
              value={String(data.basics.allowsAlcoholSales)}
              onChange={(v) => setVal(["basics", "allowsAlcoholSales"], v === "true")}
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
            />
          </Grid1>
        </Card>
      )}

      {/* 2) Location */}
      {step === 2 && (
        <Card title="Location" icon={<FiCalendar />}>
          <Grid2>
            <Field label="Building/Flat" value={data.location.address.building} onChange={(v) => setVal(["location", "address", "building"], v)} />
            <Field label="Street" value={data.location.address.street} onChange={(v) => setVal(["location", "address", "street"], v)} />
          </Grid2>
          <Grid3>
            <Field label="Area" value={data.location.address.area} onChange={(v) => setVal(["location", "address", "area"], v)} />
            <Field label="City" value={data.location.address.city} onChange={(v) => setVal(["location", "address", "city"], v)} />
            <Field label="State" value={data.location.address.state} onChange={(v) => setVal(["location", "address", "state"], v)} />
          </Grid3>
          <Grid3>
            <Field label="PIN" value={data.location.address.pin} onChange={(v) => setVal(["location", "address", "pin"], v)} />
            <Field label="Latitude" type="number" value={data.location.lat} onChange={(v) => setVal(["location", "lat"], v)} />
            <Field label="Longitude" type="number" value={data.location.lng} onChange={(v) => setVal(["location", "lng"], v)} />
          </Grid3>
          <Grid2>
            <Field label="Nearby landmark" value={data.location.landmark} onChange={(v) => setVal(["location", "landmark"], v)} />
            <div />
          </Grid2>
          <Grid4>
            <Field label="Parking cars" type="number" value={data.location.parking.cars} onChange={(v) => setVal(["location", "parking", "cars"], v)} />
            <Field label="Parking bikes" type="number" value={data.location.parking.bikes} onChange={(v) => setVal(["location", "parking", "bikes"], v)} />
            <Select
              label="Valet"
              value={String(data.location.parking.valet)}
              onChange={(v) => setVal(["location", "parking", "valet"], v)}
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
            />
            <Field label="Parking charges (₹)" type="number" value={data.location.parking.charges} onChange={(v) => setVal(["location", "parking", "charges"], v)} />
          </Grid4>
          <Grid3>
            <Field label="Nearest hospital" value={data.location.nearestHospital.name} onChange={(v) => setVal(["location", "nearestHospital", "name"], v)} />
            <Field label="Distance (km)" type="number" value={data.location.nearestHospital.distanceKm} onChange={(v) => setVal(["location", "nearestHospital", "distanceKm"], v)} />
            <Field label="Phone" value={data.location.nearestHospital.phone} onChange={(v) => setVal(["location", "nearestHospital", "phone"], v)} />
          </Grid3>
          <Grid3>
            <Field label="Nearest police station" value={data.location.nearestPoliceStation.name} onChange={(v) => setVal(["location", "nearestPoliceStation", "name"], v)} />
            <Field label="Distance (km)" type="number" value={data.location.nearestPoliceStation.distanceKm} onChange={(v) => setVal(["location", "nearestPoliceStation", "distanceKm"], v)} />
            <Field label="Phone" value={data.location.nearestPoliceStation.phone} onChange={(v) => setVal(["location", "nearestPoliceStation", "phone"], v)} />
          </Grid3>
        </Card>
      )}

      {/* 3..14 (Operations → Declarations) – SAME AS BEFORE */}
      {/* 3) Operations & Timings */}
      {step === 3 && (
        <Card title="Operations & Timings" icon={<FiCalendar />}>
          <Grid3>
            <Field label="Open time" type="time" value={data.operations.openTime} onChange={(v) => setVal(["operations", "openTime"], v)} />
            <Field label="Close time" type="time" value={data.operations.closeTime} onChange={(v) => setVal(["operations", "closeTime"], v)} />
            <Field label="Music cut-off" type="time" value={data.operations.musicCutoff} onChange={(v) => setVal(["operations", "musicCutoff"], v)} />
          </Grid3>
          <Grid1>
            <TextArea label="Blackout dates / curfew notes" value={data.operations.blackoutNotes} onChange={(v) => setVal(["operations", "blackoutNotes"], v)} rows={3} />
          </Grid1>
          <Grid1>
            <Field label="Site-visit hours (optional)" value={data.operations.siteVisitHours} onChange={(v) => setVal(["operations", "siteVisitHours"], v)} />
          </Grid1>
        </Card>
      )}

      {/* 4) Safety & Security */}
      {step === 4 && (
        <Card title="Safety & Security" icon={<FiCheckCircle />}>
          <Grid3>
            <Field label="Emergency exits" type="number" value={data.safetySecurity.emergencyExits} onChange={(v) => setVal(["safetySecurity", "emergencyExits"], v)} />
            <Select
              label="Fire alarm present"
              value={String(data.safetySecurity.fireAlarm)}
              onChange={(v) => setVal(["safetySecurity", "fireAlarm"], v)}
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
            />
            <Select
              label="CCTV coverage"
              value={String(data.safetySecurity.cctv.present)}
              onChange={(v) => setVal(["safetySecurity", "cctv", "present"], v)}
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
            />
          </Grid3>
          <Grid3>
            <Field label="CCTV cameras (count)" type="number" value={data.safetySecurity.cctv.cameraCount} onChange={(v) => setVal(["safetySecurity", "cctv", "cameraCount"], v)} />
            <Select
              label="Security staff available"
              value={String(data.safetySecurity.securityStaff.available)}
              onChange={(v) => setVal(["safetySecurity", "securityStaff", "available"], v)}
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
            />
            <Field label="Headcount per 100 guests" type="number" value={data.safetySecurity.securityStaff.per100Guests} onChange={(v) => setVal(["safetySecurity", "securityStaff", "per100Guests"], v)} />
          </Grid3>
          <Grid1>
            <FileInputMulti
              label="Insurance / NOC documents (PDF/JPG)"
              accept=".pdf,image/*"
              onChange={(fa) => setFilesVal(["safety", "insuranceDocs"], fa)}
            />
          </Grid1>
        </Card>
      )}

      {/* 5) Power & Internet */}
      {step === 5 && (
        <Card title="Power & Internet" icon={<FiCalendar />}>
          <Grid3>
            <Field label="Power supply (KVA)" type="number" value={data.powerInternet.powerKva} onChange={(v) => setVal(["powerInternet", "powerKva"], v)} />
            <Select
              label="Power backup"
              value={data.powerInternet.backup.type}
              onChange={(v) => setVal(["powerInternet", "backup", "type"], v)}
              options={[
                { value: "generator", label: "Generator" },
                { value: "ups", label: "UPS" },
                { value: "none", label: "None" },
              ]}
            />
            <Field label="Backup capacity (KVA)" type="number" value={data.powerInternet.backup.capacityKva} onChange={(v) => setVal(["powerInternet", "backup", "capacityKva"], v)} />
          </Grid3>
          <Grid3>
            <Select
              label="3-phase available"
              value={String(data.powerInternet.threePhase)}
              onChange={(v) => setVal(["powerInternet", "threePhase"], v)}
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
            />
            <Select
              label="Internet available"
              value={String(data.powerInternet.internet.available)}
              onChange={(v) => setVal(["powerInternet", "internet", "available"], v)}
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
            />
            <div />
          </Grid3>
          <Grid3>
            <Field label="Upload (Mbps)" type="number" value={data.powerInternet.internet.upMbps} onChange={(v) => setVal(["powerInternet", "internet", "upMbps"], v)} />
            <Field label="Download (Mbps)" type="number" value={data.powerInternet.internet.downMbps} onChange={(v) => setVal(["powerInternet", "internet", "downMbps"], v)} />
            <div />
          </Grid3>
        </Card>
      )}

      {/* 6) Licences & Permissions */}
      {step === 6 && (
        <Card title="Licences & Permissions" icon={<FiCheckCircle />}>
          <Grid1>
            <Field
              label="Performance/music licences required (comma-separated, e.g., PPL, Novex)"
              value={data.licences.requiredCsv}
              onChange={(v) => setVal(["licences", "requiredCsv"], v)}
            />
          </Grid1>
          <Grid2>
            <Select
              label="Police permission required"
              value={String(data.licences.policePermission.required)}
              onChange={(v) => setVal(["licences", "policePermission", "required"], v)}
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
            />
            <Select
              label="Will venue obtain police permission"
              value={data.licences.policePermission.obtainedByVenue}
              onChange={(v) => setVal(["licences", "policePermission", "obtainedByVenue"], v)}
              options={[
                { value: "yes", label: "Yes" },
                { value: "no", label: "No" },
                { value: "depends", label: "Depends" },
              ]}
            />
          </Grid2>
        </Card>
      )}

      {/* 7) Spaces / Halls */}
      {step === 7 && (
        <Card title="Spaces / Halls" icon={<FaHotel />}>
          <div style={{ display: "grid", gap: 12 }}>
            {data.spaces.length === 0 && (
              <div style={{ ...noteBox }}>
                No spaces added yet. Click “Add Space” to create your first hall/banquet/lawn.
              </div>
            )}
            {data.spaces.map((s, idx) => (
              <div key={s.id} style={spaceCard}>
                <div style={spaceHeader}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 900 }}>
                    <FaHotel /> Space #{idx + 1} — {s.name || s.id}
                  </div>
                  <button type="button" onClick={() => removeSpace(idx)} style={smallGhostBtn} title="Remove">
                    <FiTrash2 /> Remove
                  </button>
                </div>

                <Grid3>
                  <Field label="Space name/ID" value={s.name} onChange={(v) => updateSpace(idx, "name", v)} />
                  <Select
                    label="Type"
                    value={s.type}
                    onChange={(v) => updateSpace(idx, "type", v)}
                    options={[
                      { value: "indoor", label: "Indoor" },
                      { value: "outdoor", label: "Outdoor" },
                      { value: "covered", label: "Covered" },
                    ]}
                  />
                  <Field label="Total area (sq ft)" type="number" value={s.areaSqFt} onChange={(v) => updateSpace(idx, "areaSqFt", v)} />
                </Grid3>

                <Grid3>
                  <Field label="Length (m)" type="number" value={s.lengthM} onChange={(v) => updateSpace(idx, "lengthM", v)} />
                  <Field label="Width (m)" type="number" value={s.widthM} onChange={(v) => updateSpace(idx, "widthM", v)} />
                  <Field label="Height (m)" type="number" value={s.heightM} onChange={(v) => updateSpace(idx, "heightM", v)} />
                </Grid3>

                <Grid3>
                  <Select
                    label="Stage available"
                    value={String(s.stageAvailable)}
                    onChange={(v) => updateSpace(idx, "stageAvailable", v === "true")}
                    options={[
                      { value: "true", label: "Yes" },
                      { value: "false", label: "No" },
                    ]}
                  />
                  <Field label="Stage L (m)" type="number" value={s.stageL} onChange={(v) => updateSpace(idx, "stageL", v)} />
                  <Field label="Stage W (m)" type="number" value={s.stageW} onChange={(v) => updateSpace(idx, "stageW", v)} />
                </Grid3>

                <Grid3>
                  <Field label="Stage H (m)" type="number" value={s.stageH} onChange={(v) => updateSpace(idx, "stageH", v)} />
                  <Select
                    label="PA / Music system"
                    value={String(s.paSystem)}
                    onChange={(v) => updateSpace(idx, "paSystem", v === "true")}
                    options={[
                      { value: "true", label: "Yes" },
                      { value: "false", label: "No" },
                    ]}
                  />
                  <Field label="PA details" value={s.paDetails} onChange={(v) => updateSpace(idx, "paDetails", v)} />
                </Grid3>

                <Grid3>
                  <Select
                    label="LED wall available"
                    value={String(s.ledWall)}
                    onChange={(v) => updateSpace(idx, "ledWall", v === "true")}
                    options={[
                      { value: "true", label: "Yes" },
                      { value: "false", label: "No" },
                    ]}
                  />
                  <Field label="LED Width (m)" type="number" value={s.ledWallW} onChange={(v) => updateSpace(idx, "ledWallW", v)} />
                  <Field label="LED Height (m)" type="number" value={s.ledWallH} onChange={(v) => updateSpace(idx, "ledWallH", v)} />
                </Grid3>

                <Grid2>
                  <Field label="LED Resolution (px e.g., 1920x1080)" value={s.ledWallRes} onChange={(v) => updateSpace(idx, "ledWallRes", v)} />
                  <Select
                    label="In-house décor"
                    value={String(s.inHouseDecor)}
                    onChange={(v) => updateSpace(idx, "inHouseDecor", v === "true")}
                    options={[
                      { value: "true", label: "Yes" },
                      { value: "false", label: "No" },
                    ]}
                  />
                </Grid2>

                <Grid3>
                  <Field label="Tables" type="number" value={s.tables} onChange={(v) => updateSpace(idx, "tables", v)} />
                  <Field label="Chairs" type="number" value={s.chairs} onChange={(v) => updateSpace(idx, "chairs", v)} />
                  <div />
                </Grid3>

                <Grid3>
                  <Field label="Layout: Theatre" type="number" value={s.layoutTheatre} onChange={(v) => updateSpace(idx, "layoutTheatre", v)} />
                  <Field label="Layout: Banquet" type="number" value={s.layoutBanquet} onChange={(v) => updateSpace(idx, "layoutBanquet", v)} />
                  <Field label="Layout: Classroom" type="number" value={s.layoutClassroom} onChange={(v) => updateSpace(idx, "layoutClassroom", v)} />
                </Grid3>

                <Grid3>
                  <Field label="Layout: U-shape" type="number" value={s.layoutU} onChange={(v) => updateSpace(idx, "layoutU", v)} />
                  <Field label="Layout: Cabaret" type="number" value={s.layoutCabaret} onChange={(v) => updateSpace(idx, "layoutCabaret", v)} />
                  <Field label="Layout: Standing" type="number" value={s.layoutStanding} onChange={(v) => updateSpace(idx, "layoutStanding", v)} />
                </Grid3>

                <Grid3>
                  <Field label="Seating capacity (max)" type="number" value={s.seatingCapacity} onChange={(v) => updateSpace(idx, "seatingCapacity", v)} />
                  <Field label="Standing capacity (max)" type="number" value={s.standingCapacity} onChange={(v) => updateSpace(idx, "standingCapacity", v)} />
                  <Field label="Total guest capacity (exclude staff)" type="number" value={s.totalCapacityGuests} onChange={(v) => updateSpace(idx, "totalCapacityGuests", v)} />
                </Grid3>

                <Grid3>
                  <Field label="Green rooms" type="number" value={s.greenRooms} onChange={(v) => updateSpace(idx, "greenRooms", v)} />
                  <Field label="Restrooms (Male)" type="number" value={s.restMale} onChange={(v) => updateSpace(idx, "restMale", v)} />
                  <Field label="Restrooms (Female)" type="number" value={s.restFemale} onChange={(v) => updateSpace(idx, "restFemale", v)} />
                </Grid3>

                <Grid2>
                  <Field label="Accessible restrooms" type="number" value={s.restAccessible} onChange={(v) => updateSpace(idx, "restAccessible", v)} />
                  <div />
                </Grid2>

                <Grid2>
                  <Select
                    label="Accessibility: Ramp"
                    value={String(s.accRamp)}
                    onChange={(v) => updateSpace(idx, "accRamp", v === "true")}
                    options={[
                      { value: "true", label: "Yes" },
                      { value: "false", label: "No" },
                    ]}
                  />
                  <Select
                    label="Accessibility: Lift to floor"
                    value={String(s.accLift)}
                    onChange={(v) => updateSpace(idx, "accLift", v === "true")}
                    options={[
                      { value: "true", label: "Yes" },
                      { value: "false", label: "No" },
                    ]}
                  />
                </Grid2>

                <Grid1>
                  <TextArea label="Notes (pillars/obstructions etc.)" value={s.notes} onChange={(v) => updateSpace(idx, "notes", v)} rows={2} />
                </Grid1>
              </div>
            ))}

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="button" onClick={addSpace} style={primaryBtn}>
                <FiPlusCircle /> Add Space
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* 8) Staffing */}
      {step === 8 && (
        <Card title="Staffing" icon={<FiCheckCircle />}>
          <Grid3>
            <Field label="Serving staff count" type="number" value={data.staffing.servingStaffCount} onChange={(v) => setVal(["staffing", "servingStaffCount"], v)} />
            <Field label="Technical staff count" type="number" value={data.staffing.technicalStaffCount} onChange={(v) => setVal(["staffing", "technicalStaffCount"], v)} />
            <Select
              label="Housekeeping during event"
              value={String(data.staffing.housekeepingDuringEvent)}
              onChange={(v) => setVal(["staffing", "housekeepingDuringEvent"], v === "true")}
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
            />
          </Grid3>
          <Grid1>
            <Field label="Housekeeping frequency" value={data.staffing.housekeepingFrequency} onChange={(v) => setVal(["staffing", "housekeepingFrequency"], v)} />
          </Grid1>
        </Card>
      )}

      {/* 9) Pricing & Rental Rules */}
      {step === 9 && (
        <Card title="Pricing & Rental Rules" icon={<FiCalendar />}>
          <Grid2>
            <Checkbox label="Allow Half-day" checked={!!data.pricing.allowedSlots.halfDay} onChange={(v) => setVal(["pricing", "allowedSlots", "halfDay"], v)} />
            <Checkbox label="Allow Full-day" checked={!!data.pricing.allowedSlots.fullDay} onChange={(v) => setVal(["pricing", "allowedSlots", "fullDay"], v)} />
          </Grid2>
          <Grid3>
            <Field label="Half-day slot 1 start" type="time" value={data.pricing.halfDay.slot1.start} onChange={(v) => setVal(["pricing", "halfDay", "slot1", "start"], v)} />
            <Field label="Half-day slot 1 end" type="time" value={data.pricing.halfDay.slot1.end} onChange={(v) => setVal(["pricing", "halfDay", "slot1", "end"], v)} />
            <div />
          </Grid3>
          <Grid3>
            <Field label="Half-day slot 2 start" type="time" value={data.pricing.halfDay.slot2.start} onChange={(v) => setVal(["pricing", "halfDay", "slot2", "start"], v)} />
            <Field label="Half-day slot 2 end" type="time" value={data.pricing.halfDay.slot2.end} onChange={(v) => setVal(["pricing", "halfDay", "slot2", "end"], v)} />
            <div />
          </Grid3>
          <Grid3>
            <Field label="Full-day start" type="time" value={data.pricing.fullDay.start} onChange={(v) => setVal(["pricing", "fullDay", "start"], v)} />
            <Field label="Full-day end" type="time" value={data.pricing.fullDay.end} onChange={(v) => setVal(["pricing", "fullDay", "end"], v)} />
            <div />
          </Grid3>
          <Grid4>
            <Field label="Weekday Half-day (₹)" type="number" value={data.pricing.basePrice.weekday.halfDay} onChange={(v) => setVal(["pricing", "basePrice", "weekday", "halfDay"], v)} />
            <Field label="Weekday Full-day (₹)" type="number" value={data.pricing.basePrice.weekday.fullDay} onChange={(v) => setVal(["pricing", "basePrice", "weekday", "fullDay"], v)} />
            <Field label="Weekend Half-day (₹)" type="number" value={data.pricing.basePrice.weekend.halfDay} onChange={(v) => setVal(["pricing", "basePrice", "weekend", "halfDay"], v)} />
            <Field label="Weekend Full-day (₹)" type="number" value={data.pricing.basePrice.weekend.fullDay} onChange={(v) => setVal(["pricing", "basePrice", "weekend", "fullDay"], v)} />
          </Grid4>
          <Grid1>
            <Field label="What's included" value={data.pricing.includes} onChange={(v) => setVal(["pricing", "includes"], v)} />
          </Grid1>
          <Grid3>
            <Field label="Overtime rate (₹/hr)" type="number" value={data.pricing.overtimeRatePerHour} onChange={(v) => setVal(["pricing", "overtimeRatePerHour"], v)} />
            <Field label="Security deposit (₹)" type="number" value={data.pricing.securityDeposit} onChange={(v) => setVal(["pricing", "securityDeposit"], v)} />
            <div />
          </Grid3>

          <div style={{ marginTop: 6 }}>
            <div style={{ fontWeight: 800, color: COLORS.text, marginBottom: 6 }}>Cancellation / Reschedule Policy</div>
            <div style={{ display: "grid", gap: 10 }}>
              {data.pricing.cancellationPolicy.map((r, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10 }}>
                  <Field label="Days before" type="number" value={r.daysBefore} onChange={(v) => setVal(["pricing", "cancellationPolicy", i, "daysBefore"], v)} />
                  <Field label="Refund %" type="number" value={r.refundPercent} onChange={(v) => setVal(["pricing", "cancellationPolicy", i, "refundPercent"], v)} />
                  <button type="button" style={smallGhostBtn} onClick={() => removeCancelRow(i)}>
                    <FiTrash2 /> Remove
                  </button>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button type="button" style={primaryBtn} onClick={addCancelRow}>
                  <FiPlusCircle /> Add Row
                </button>
              </div>
            </div>
          </div>

          <Grid3>
            <Field label="Setup buffer (min)" type="number" value={data.pricing.setupBufferMinutes} onChange={(v) => setVal(["pricing", "setupBufferMinutes"], v)} />
            <Field label="Teardown buffer (min)" type="number" value={data.pricing.teardownBufferMinutes} onChange={(v) => setVal(["pricing", "teardownBufferMinutes"], v)} />
            <div />
          </Grid3>
          <Grid3>
            <Field label="GST %" type="number" value={data.pricing.taxes.gstPercent} onChange={(v) => setVal(["pricing", "taxes", "gstPercent"], v)} />
            <Field label="Service charge %" type="number" value={data.pricing.taxes.serviceChargePercent} onChange={(v) => setVal(["pricing", "taxes", "serviceChargePercent"], v)} />
            <div />
          </Grid3>
        </Card>
      )}

      {/* 10) F&B & Bar */}
      {step === 10 && (
        <Card title="F&B & Bar" icon={<FiCalendar />}>
          <Grid3>
            <Select
              label="In-house catering"
              value={String(data.fnbBar.inHouseCatering)}
              onChange={(v) => setVal(["fnbBar", "inHouseCatering"], v)}
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
            />
            <Field label="Cuisines (comma-separated)" value={data.fnbBar.cuisinesCsv} onChange={(v) => setVal(["fnbBar", "cuisinesCsv"], v)} />
            <Select
              label="Outside catering allowed"
              value={String(data.fnbBar.outsideCateringAllowed)}
              onChange={(v) => setVal(["fnbBar", "outsideCateringAllowed"], v)}
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
            />
          </Grid3>
          <Grid3>
            <Field label="Corkage/usage fees (₹)" type="number" value={data.fnbBar.outsideCorkage} onChange={(v) => setVal(["fnbBar", "outsideCorkage"], v)} />
            <Select
              label="Kitchen type"
              value={data.fnbBar.kitchen.type}
              onChange={(v) => setVal(["fnbBar", "kitchen", "type"], v)}
              options={[
                { value: "finishing", label: "Finishing" },
                { value: "cold", label: "Cold" },
                { value: "hot", label: "Hot" },
              ]}
            />
            <Select
              label="Kitchen fuel"
              value={data.fnbBar.kitchen.fuel}
              onChange={(v) => setVal(["fnbBar", "kitchen", "fuel"], v)}
              options={[
                { value: "gas", label: "Gas" },
                { value: "electric", label: "Electric" },
              ]}
            />
          </Grid3>
          <Grid3>
            <Select
              label="Alcohol allowed"
              value={String(data.fnbBar.alcohol.allowed)}
              onChange={(v) => setVal(["fnbBar", "alcohol", "allowed"], v)}
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
            />
            <Select
              label="Bar licence"
              value={String(data.fnbBar.alcohol.barLicense)}
              onChange={(v) => setVal(["fnbBar", "alcohol", "barLicense"], v)}
              options={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
            />
            <Field label="Last pour time" type="time" value={data.fnbBar.alcohol.lastPourTime} onChange={(v) => setVal(["fnbBar", "alcohol", "lastPourTime"], v)} />
          </Grid3>

          <Grid2>
            <Field
              label="Bar corkage per bottle (₹)"
              type="number"
              value={data.fnbBar.barCharges.corkagePerBottle}
              onChange={(v) => setVal(["fnbBar", "barCharges", "corkagePerBottle"], v)}
            />
            <Field
              label="Bartender per shift (₹)"
              type="number"
              value={data.fnbBar.barCharges.bartenderPerShift}
              onChange={(v) => setVal(["fnbBar", "barCharges", "bartenderPerShift"], v)}
            />
          </Grid2>

          <div style={{ marginTop: 6 }}>
            <div style={{ fontWeight: 800, color: COLORS.text, marginBottom: 6 }}>Per-head packages</div>
            {data.fnbBar.perHeadPackages.map((p, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, marginBottom: 8 }}>
                <Field label="Name" value={p.name} onChange={(v) => setVal(["fnbBar", "perHeadPackages", i, "name"], v)} />
                <Field label="Price (₹)" type="number" value={p.price} onChange={(v) => setVal(["fnbBar", "perHeadPackages", i, "price"], v)} />
                <button type="button" style={smallGhostBtn} onClick={() => removePackage(i)}>
                  <FiTrash2 /> Remove
                </button>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="button" style={primaryBtn} onClick={addPackage}>
                <FiPlusCircle /> Add Package
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* 11) Media (Photos & Files) */}
      {step === 11 && (
        <Card title="Media (Photos & Files)" icon={<FiCalendar />}>
          <Grid2>
            <FileInput label="Venue logo (JPG/PNG)" accept="image/*" onChange={(fa) => setFilesVal(["media", "logo"], fa[0] || null)} />
            <FileInput label="Hero banner (JPG/PNG)" accept="image/*" onChange={(fa) => setFilesVal(["media", "heroBanner"], fa[0] || null)} />
          </Grid2>
          <Grid4>
            <FileInputMulti label="Entrance photos" accept="image/*" onChange={(fa) => setFilesVal(["media", "entrance"], fa)} />
            <FileInputMulti label="Outdoor photos" accept="image/*" onChange={(fa) => setFilesVal(["media", "outdoor"], fa)} />
            <FileInputMulti label="Front side photos" accept="image/*" onChange={(fa) => setFilesVal(["media", "frontSide"], fa)} />
            <FileInputMulti label="Banquet hall photos" accept="image/*" onChange={(fa) => setFilesVal(["media", "banquetHalls"], fa)} />
          </Grid4>
          <Grid1>
            <FileInputMulti label="Gallery (min 6 images recommended)" accept="image/*" onChange={(fa) => setFilesVal(["media", "gallery"], fa)} />
          </Grid1>
          <Grid1>
            <FileInputMulti label="Floor plans (PDF/JPG)" accept=".pdf,image/*" onChange={(fa) => setFilesVal(["media", "floorPlans"], fa)} />
          </Grid1>
        </Card>
      )}

      {/* 12) Payouts & Billing */}
      {step === 12 && (
        <Card title="Payouts & Billing" icon={<FiCalendar />}>
          <Grid2>
            <Field label="Beneficiary (legal entity)" value={data.payoutsBilling.beneficiaryName} onChange={(v) => setVal(["payoutsBilling", "beneficiaryName"], v)} />
            <Field label="Billing address" value={data.payoutsBilling.billingAddress} onChange={(v) => setVal(["payoutsBilling", "billingAddress"], v)} />
          </Grid2>
          <Grid3>
            <Field label="Account name" value={data.payoutsBilling.bank.accountName} onChange={(v) => setVal(["payoutsBilling", "bank", "accountName"], v)} />
            <Field label="Account number" value={data.payoutsBilling.bank.accountNumber} onChange={(v) => setVal(["payoutsBilling", "bank", "accountNumber"], v)} />
            <Field label="IFSC" value={data.payoutsBilling.bank.ifsc} onChange={(v) => setVal(["payoutsBilling", "bank", "ifsc"], v)} />
          </Grid3>
          <Grid2>
            <Field label="UPI ID (optional)" value={data.payoutsBilling.upiId} onChange={(v) => setVal(["payoutsBilling", "upiId"], v)} />
            <Select
              label="Invoice issuer"
              value={data.payoutsBilling.invoiceIssuer}
              onChange={(v) => setVal(["payoutsBilling", "invoiceIssuer"], v)}
              options={[
                { value: "Venue", label: "Venue" },
                { value: "Platform", label: "Platform" },
                { value: "Both", label: "Both" },
              ]}
            />
          </Grid2>
        </Card>
      )}

      {/* 13) Contacts & Notifications */}
      {step === 13 && (
        <Card title="Contacts & Notifications" icon={<FiCalendar />}>
          <Grid3>
            <Field label="Booking contact name" value={data.contacts.booking.name} onChange={(v) => setVal(["contacts", "booking", "name"], v)} />
            <Field label="Phone" value={data.contacts.booking.phone} onChange={(v) => setVal(["contacts", "booking", "phone"], v)} />
            <Field label="Email" value={data.contacts.booking.email} onChange={(v) => setVal(["contacts", "booking", "email"], v)} />
          </Grid3>
          <Grid3>
            <Field label="Finance contact name" value={data.contacts.finance.name} onChange={(v) => setVal(["contacts", "finance", "name"], v)} />
            <Field label="Phone" value={data.contacts.finance.phone} onChange={(v) => setVal(["contacts", "finance", "phone"], v)} />
            <Field label="Email" value={data.contacts.finance.email} onChange={(v) => setVal(["contacts", "finance", "email"], v)} />
          </Grid3>
          <Grid3>
            <Field label="Technical contact name" value={data.contacts.technical.name} onChange={(v) => setVal(["contacts", "technical", "name"], v)} />
            <Field label="Phone" value={data.contacts.technical.phone} onChange={(v) => setVal(["contacts", "technical", "phone"], v)} />
            <Field label="Email" value={data.contacts.technical.email} onChange={(v) => setVal(["contacts", "technical", "email"], v)} />
          </Grid3>
          <Grid1>
            <Field label="WhatsApp number for updates (optional)" value={data.contacts.whatsappUpdates} onChange={(v) => setVal(["contacts", "whatsappUpdates"], v)} />
          </Grid1>
        </Card>
      )}

      {/* 14) Declarations */}
      {step === 14 && (
        <Card title="Declarations" icon={<FiCheckCircle />}>
          <Grid1>
            <Checkbox
              label="Confirm capacities exclude staff headcount"
              checked={data.declarations.capacityExcludesStaff}
              onChange={(v) => setVal(["declarations", "capacityExcludesStaff"], v)}
            />
            <Checkbox label="Accept terms & commission" checked={data.declarations.termsAccepted} onChange={(v) => setVal(["declarations", "termsAccepted"], v)} />
            <Checkbox label="Data & media consent" checked={data.declarations.mediaConsent} onChange={(v) => setVal(["declarations", "mediaConsent"], v)} />
          </Grid1>
        </Card>
      )}

      {/* Navigation */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
        <button type="button" onClick={back} style={ghostBtn} disabled={step === 0}>
          Back
        </button>
        {step < total - 1 ? (
          <button type="button" onClick={next} style={primaryBtn}>
            Next
          </button>
        ) : (
          <button type="submit" disabled={saving} style={primaryBtn}>
            {saving ? "Saving…" : "Register Venue"}
          </button>
        )}
      </div>

      {/* Success Popup */}
      {showThanks && (
        <div style={modalBackdrop}>
          <div style={modalCard} role="dialog" aria-modal="true" aria-labelledby="thanks-title">
            <div style={modalHeader}>
              <div id="thanks-title" style={{ fontWeight: 900, color: "#fff" }}>
                <FiCheckCircle style={{ marginRight: 6 }} /> Thank you!
              </div>
              <button type="button" onClick={() => setShowThanks(false)} style={modalCloseBtn} aria-label="Close">
                <FiX />
              </button>
            </div>
            <div style={{ padding: 16, color: COLORS.text }}>
              <p style={{ margin: 0 }}>
                Thanks for registration. Our team will verify and make your venue live soon.
              </p>
              <p style={{ margin: "6px 0 0" }}>
                Thanks for choosing <b>apnaartist</b>.
              </p>
            </div>
            <div style={{ padding: 16, display: "flex", justifyContent: "flex-end" }}>
              <button type="button" onClick={() => setShowThanks(false)} style={primaryBtn}>
                Okay
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Already Registered Popup */}
      {showAlready && (
        <div style={modalBackdrop}>
          <div style={modalCard} role="dialog" aria-modal="true" aria-labelledby="already-title">
            <div style={modalHeader}>
              <div id="already-title" style={{ fontWeight: 900, color: "#fff" }}>
                <FiHash style={{ marginRight: 6 }} /> Already registered
              </div>
              <button type="button" onClick={() => setShowAlready(false)} style={modalCloseBtn} aria-label="Close">
                <FiX />
              </button>
            </div>
            <div style={{ padding: 16, color: COLORS.text }}>
              <p style={{ margin: 0 }}>
                This venue (<b>{data.ownership.venuePublicName}</b>) is already registered for your account.
              </p>
              <p style={{ margin: "6px 0 0" }}>Please go to Manage Venue to view or update it.</p>
            </div>
            <div style={{ padding: 16, display: "flex", justifyContent: "flex-end" }}>
              <button type="button" onClick={() => setShowAlready(false)} style={primaryBtn}>
                Okay
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );

  /* ── Local helpers for repeaters ─────────────────────── */
  function addSpace() {
    setData((prev) => ({
      ...prev,
      spaces: [
        ...prev.spaces,
        {
          id: `SPACE-${prev.spaces.length + 1}`,
          name: "",
          type: "indoor",
          areaSqFt: "",
          lengthM: "",
          widthM: "",
          heightM: "",
          stageAvailable: false,
          stageL: "",
          stageW: "",
          stageH: "",
          paSystem: false,
          paDetails: "",
          ledWall: false,
          ledWallW: "",
          ledWallH: "",
          ledWallRes: "",
          inHouseDecor: false,
          tables: "",
          chairs: "",
          layoutTheatre: "",
          layoutBanquet: "",
          layoutClassroom: "",
          layoutU: "",
          layoutCabaret: "",
          layoutStanding: "",
          seatingCapacity: "",
          standingCapacity: "",
          totalCapacityGuests: "",
          greenRooms: "",
          restMale: "",
          restFemale: "",
          restAccessible: "",
          accRamp: false,
          accLift: false,
          notes: "",
        },
      ],
    }));
  }
  function removeSpace(index) {
    setData((prev) => ({ ...prev, spaces: prev.spaces.filter((_, i) => i !== index) }));
  }
  function updateSpace(index, key, value) {
    setData((prev) => {
      const copy = [...prev.spaces];
      copy[index] = { ...copy[index], [key]: value };
      return { ...prev, spaces: copy };
    });
  }
  function addCancelRow() {
    setData((prev) => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        cancellationPolicy: [...prev.pricing.cancellationPolicy, { daysBefore: "", refundPercent: "" }],
      },
    }));
  }
  function removeCancelRow(i) {
    setData((prev) => ({
      ...prev,
      pricing: { ...prev.pricing, cancellationPolicy: prev.pricing.cancellationPolicy.filter((_, idx) => idx !== i) },
    }));
  }
  function addPackage() {
    setData((prev) => ({
      ...prev,
      fnbBar: { ...prev.fnbBar, perHeadPackages: [...prev.fnbBar.perHeadPackages, { name: "", price: "" }] },
    }));
  }
  function removePackage(i) {
    setData((prev) => ({
      ...prev,
      fnbBar: { ...prev.fnbBar, perHeadPackages: prev.fnbBar.perHeadPackages.filter((_, idx) => idx !== i) },
    }));
  }
}

/* ──────────────────────────────────────────────────────────
   Shared UI bits — compact spacing, gaps, responsive grids
   ────────────────────────────────────────────────────────── */
function ProgressBar({ step, total, label }) {
  const pct = Math.round(((step + 1) / total) * 100);
  return (
    <div style={{ position: "sticky", top: 0, zIndex: 5, background: COLORS.page, padding: "8px 0 6px" }}>
      <div style={{ fontSize: 13, color: COLORS.subtext, marginBottom: 6, fontWeight: 700 }}>
        Step {step + 1} of {total} — {label}
      </div>
      <div
        style={{
          width: "100%",
          height: 10,
          background: "#ffffff",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 999,
          overflow: "hidden",
          boxShadow: "inset 0 1px 2px rgba(0,0,0,.04)",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
            transition: "width .35s ease",
          }}
        />
      </div>
    </div>
  );
}

const wizardWrap = {
  maxWidth: 1100,
  margin: "0 auto",
  display: "grid",
  gap: 16,
  padding: "12px 8px 24px",
  boxSizing: "border-box",
};

function Card({ title, icon, children }) {
  return (
    <section
      style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 14,
        boxShadow: "0 1px 2px rgba(0,0,0,.05)",
        padding: 16,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div
          style={{
            width: 34,
            height: 34,
            display: "grid",
            placeItems: "center",
            borderRadius: 8,
            background: "#fff",
            border: `1px solid ${COLORS.border}`,
            color: COLORS.primary,
          }}
        >
          {icon}
        </div>
        <div style={{ fontWeight: 900, fontSize: 18, color: COLORS.text }}>{title}</div>
      </div>
      <div style={{ display: "grid", gap: 12 }}>{children}</div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", required = false }) {
  return (
    <label style={{ display: "block" }}>
      <span style={labelStyle}>
        {label}
        {required && <span style={{ color: COLORS.primary }}> *</span>}
      </span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} style={inputStyle} />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 3 }) {
  return (
    <label style={{ display: "block" }}>
      <span style={labelStyle}>{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        style={{ ...inputStyle, height: rows * 20 + 24, paddingTop: 8, paddingBottom: 8, resize: "vertical" }}
      />
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label style={{ display: "block" }}>
      <span style={labelStyle}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Checkbox({ label, checked, onChange }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span style={labelStyle}>{label}</span>
    </label>
  );
}

/* File inputs */
function FileInput({ label, accept, onChange }) {
  return (
    <label style={{ display: "block" }}>
      <span style={labelStyle}>{label}</span>
      <input type="file" accept={accept} onChange={(e) => onChange(Array.from(e.target.files || []))} style={inputStyle} />
    </label>
  );
}
function FileInputMulti({ label, accept, onChange }) {
  return (
    <label style={{ display: "block" }}>
      <span style={labelStyle}>{label}</span>
      <input type="file" multiple accept={accept} onChange={(e) => onChange(Array.from(e.target.files || []))} style={inputStyle} />
    </label>
  );
}

const Grid1 = ({ children }) => <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>{children}</div>;
const Grid2 = ({ children }) => <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>;
const Grid3 = ({ children }) => <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>{children}</div>;
const Grid4 = ({ children }) => <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>{children}</div>;

const inputStyle = {
  width: "100%",
  height: 42,
  padding: "0 12px",
  marginTop: 6,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  outline: "none",
  background: "#fff",
  color: COLORS.text,
  boxSizing: "border-box",
};

const labelStyle = { fontSize: 13, color: COLORS.text };

const primaryBtn = {
  height: 42,
  padding: "0 14px",
  borderRadius: 10,
  border: "none",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
};

const ghostBtn = {
  height: 42,
  padding: "0 14px",
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  color: COLORS.text,
  background: "#fff",
  fontWeight: 700,
  cursor: "pointer",
};

const smallGhostBtn = {
  height: 36,
  padding: "0 12px",
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  color: COLORS.text,
  background: "#fff",
  fontWeight: 700,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const errorBox = {
  color: "#b91c1c",
  background: "#fee2e2",
  border: "1px solid #fecaca",
  padding: 12,
  borderRadius: 10,
};

const noteBox = {
  color: COLORS.subtext,
  background: "#f8fafc",
  border: `1px solid ${COLORS.border}`,
  padding: 12,
  borderRadius: 10,
};

const spaceCard = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  background: "#fff",
  padding: 12,
  display: "grid",
  gap: 10,
};

const spaceHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

/* Modals */
const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.45)",
  zIndex: 80,
  display: "grid",
  placeItems: "center",
  padding: 16,
};
const modalCard = {
  width: "min(560px, 96vw)",
  borderRadius: 14,
  background: "#fff",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,.25)",
};
const modalHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: 12,
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
};
const modalCloseBtn = {
  border: `1px solid rgba(255,255,255,0.5)`,
  background: "transparent",
  color: "#fff",
  height: 34,
  width: 34,
  borderRadius: 8,
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
};

/* Drawer styles */
const drawerBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.45)",
  zIndex: 80,
  display: "block",
};
const drawerPanelBase = {
  position: "fixed",
  top: 0,
  right: 0,
  height: "100vh",
  width: "50vw",
  maxWidth: 720,
  background: "#fff",
  borderLeft: `1px solid ${COLORS.border}`,
  boxShadow: "0 10px 30px rgba(0,0,0,.25)",
  display: "flex",
  flexDirection: "column",
  transition: "transform .22s ease-out",
};
const drawerPanel = { ...drawerPanelBase };
const drawerHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: 12,
  borderBottom: `1px solid ${COLORS.border}`,
};
const drawerCloseBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  height: 36,
  width: 36,
  borderRadius: 8,
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
};
const drawerBody = {
  padding: 12,
  overflowY: "auto",
  flex: 1,
};

/* Helpers */
function deepSet(obj, path, value) {
  const keys = Array.isArray(path) ? path : String(path).split(".");
  let cur = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    if (cur[k] == null || typeof cur[k] !== "object") cur[k] = {};
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return obj;
}
function sanitizeFileName(name) {
  if (!name) return `file-${Date.now()}`;
  const lastDot = name.lastIndexOf(".");
  const baseRaw = lastDot > -1 ? name.slice(0, lastDot) : name;
  const extRaw = lastDot > -1 ? name.slice(lastDot).toLowerCase() : "";
  const base =
    baseRaw.replace(/[^a-zA-Z0-9_\- ]+/g, "").replace(/\s+/g, "_") || "file";
  return base + extRaw;
}
